import React, {useCallback} from 'react';
import {Typography} from '../typography';
import {ButtonWrapper} from './Button.styles';
import {ArrowLeft, ArrowRight, ArrowUpRight} from '../icons';
import {ButtonProps as MatButtonProps} from '@material-ui/core/Button';
import {AccentColorOptions, useIsTwinmotionTheme} from 'epic-ue-theme';
import {UeLink} from '../../wraps';
import useTheme from '@material-ui/core/styles/useTheme';
import {UeThemeOptions} from 'epic-ue-theme/src/ueTheme.types';

type MatButtonPropsOmitted = Omit<MatButtonProps, 'color' | 'type'>;
export type ButtonTypeOptions = 'primary' | 'secondary' | 'tertiary';

export interface CmsButtonProps {
    label?: string;
    href?: string;
    type?: ButtonTypeOptions;
    target?: string;
}

export type ClickEventHandler = (event: React.MouseEvent) => void;
export interface ButtonProps {
    children?: string | React.ReactNode;
    color?: AccentColorOptions;
    type?: ButtonTypeOptions;
    arrowLeft?: boolean;
    arrowRight?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    href?: string;
    big?: boolean;
    onClick?: ClickEventHandler;
    id?: string;
    target?: string;
    download?: boolean | string;
}

export const Button = ({
    children,
    color = 'sky',
    type = 'primary',
    arrowLeft,
    arrowRight,
    startIcon,
    endIcon,
    href,
    onClick,
    id,
    big = true,
    tabIndex,
    target,
    ...rest
}: ButtonProps & MatButtonPropsOmitted): JSX.Element => {
    const theme = useTheme<UeThemeOptions>();
    const isTwinmotion = useIsTwinmotionTheme();
    const ueLinkCallback = useCallback(
        (e) => {
            if (onClick && typeof onClick === 'function') {
                onClick(e);
            }
        },
        [onClick]
    );

    const accentColor =
        color === 'white' ? theme?.palette?.common?.white : theme?.palette?.accent?.[color];
    const fromColor =
        color === 'white' ? theme?.palette?.common?.white : theme?.palette?.gradient?.[color]?.from;
    const toColor =
        color === 'white' ? theme?.palette?.common?.white : theme?.palette?.gradient?.[color]?.to;

    const ArrowRightIcon = type === 'tertiary' ? ArrowUpRight : ArrowRight;

    return (
        <UeLink
            tabIndex={tabIndex}
            href={href}
            target={target}
            onClick={ueLinkCallback}
            id={id || ''}
        >
            <ButtonWrapper
                {...rest}
                color={color}
                startIcon={startIcon}
                endIcon={endIcon}
                classes={{root: type, label: 'label', startIcon: 'icon', endIcon: 'icon'}}
                className={`${rest.className || ''} ${isTwinmotion ? 'twinmotion' : ''}`}
                accentColor={accentColor}
                tabIndex={-1}
                fromColor={fromColor}
                toColor={toColor}
            >
                <div className="underline container">
                    {arrowLeft ? <ArrowLeft classes={{root: 'arrow arrow-left'}} /> : null}
                    <Typography variant="button" big={big}>
                        {children}
                    </Typography>
                    {arrowRight ? <ArrowRightIcon classes={{root: 'arrow arrow-right'}} /> : null}
                </div>
            </ButtonWrapper>
        </UeLink>
    );
};
